<template>
    <div class="admin-categories">
        <header class="ac-header">
            <div>
                <span class="ac-eyebrow">{{ t('Admin', 'நிர்வாகம்') }}</span>
                <h1>{{ t('Categories', 'வகைகள்') }}</h1>
                <p>{{ t('Create, rename, delete, and organise categories and their sub-categories.', 'வகைகளையும் அவற்றின் துணை வகைகளையும் உருவாக்கவும், மறுபெயரிடவும், நீக்கவும், ஒழுங்கமைக்கவும்.') }}</p>
            </div>
            <button class="ac-refresh" :disabled="loading" @click="fetchCategories">
                <vue-feather type="refresh-cw" size="16" :animation="loading ? 'spin' : null"></vue-feather>
                {{ t('Refresh', 'புதுப்பி') }}
            </button>
        </header>

        <!-- Create -->
        <section class="ac-create">
            <h3>{{ t('Add a category', 'வகையைச் சேர்') }}</h3>
            <div class="ac-create-row">
                <input v-model="newCategory.nameEN" :placeholder="t('English name', 'ஆங்கிலப் பெயர்')" />
                <input v-model="newCategory.nameTA" :placeholder="t('Tamil name', 'தமிழ்ப் பெயர்')" />
                <button class="ac-primary" :disabled="creating || isInvalidForm" @click="createCategory">
                    <vue-feather v-if="creating" type="loader" animation="spin" size="15"></vue-feather>
                    <vue-feather v-else type="plus" size="15"></vue-feather>
                    {{ t('Create', 'உருவாக்கு') }}
                </button>
            </div>
        </section>

        <!-- Search + count -->
        <div class="ac-toolbar">
            <div class="ac-search">
                <vue-feather type="search" size="16"></vue-feather>
                <input v-model="search" :placeholder="t('Search categories', 'வகைகளைத் தேடு')" />
            </div>
            <span class="ac-count">{{ filteredParents.length }} / {{ parentCategories.length }}</span>
        </div>

        <div v-if="alert" :class="['ac-alert', alert.type]">
            <vue-feather :type="alert.type === 'error' ? 'alert-circle' : 'check-circle'" size="16"></vue-feather>
            <span>{{ alert.text }}</span>
        </div>

        <div v-if="loading" class="ac-state">
            <vue-feather type="loader" animation="spin" size="28"></vue-feather>
            <span>{{ t('Loading categories...', 'வகைகளை ஏற்றுகிறது...') }}</span>
        </div>

        <div v-else-if="filteredParents.length === 0" class="ac-state">
            <vue-feather type="grid" size="34" stroke-width="1.6"></vue-feather>
            <span>{{ t('No categories found.', 'வகைகள் எதுவும் இல்லை.') }}</span>
        </div>

        <!-- Parent categories, each with its sub-categories nested inside -->
        <div v-else class="ac-list">
            <article v-for="parent in filteredParents" :key="parent.id" class="ac-card">
                <div class="ac-card-main">
                    <div class="ac-fields">
                        <label>
                            <span>EN</span>
                            <input v-model="edits[parent.id].nameEN" />
                        </label>
                        <label>
                            <span>TA</span>
                            <input v-model="edits[parent.id].nameTA" />
                        </label>
                    </div>
                    <label class="ac-switch" :title="t('Show this category on the home page', 'இந்த வகையை முகப்பில் காட்டு')">
                        <input type="checkbox" :checked="!!parent.isFeatured" :disabled="busyId === parent.id" @change="toggleFeatured(parent)" />
                        <span class="ac-switch-track"><span class="ac-switch-thumb"></span></span>
                        <span class="ac-switch-text">{{ t('Featured', 'சிறப்பு') }}</span>
                    </label>
                    <div class="ac-card-actions">
                        <button class="ac-save" :disabled="!isChanged(parent) || busyId === parent.id" @click="saveCategory(parent)">
                            <vue-feather v-if="busyId === parent.id" type="loader" animation="spin" size="14"></vue-feather>
                            <vue-feather v-else type="save" size="14"></vue-feather>
                            {{ t('Save', 'சேமி') }}
                        </button>
                        <button class="ac-delete" :disabled="busyId === parent.id" @click="deleteCategory(parent)" :title="t('Delete', 'நீக்கு')">
                            <vue-feather type="trash-2" size="14"></vue-feather>
                        </button>
                    </div>
                </div>
                <button class="ac-id" :title="t('Click to copy', 'நகலெடுக்க கிளிக் செய்யவும்')" @click="copyId(parent.id)">ID: {{ parent.id }}</button>

                <div class="ac-subs">
                    <div class="ac-subs-head">
                        <span class="ac-subs-label">{{ t('Sub-categories', 'துணை வகைகள்') }}</span>
                        <div class="ac-add-sub">
                            <select v-model="assignSel[parent.id]">
                                <option :value="null" disabled>{{ t('Add sub-category…', 'துணை வகையைச் சேர்…') }}</option>
                                <option v-for="opt in assignableFor(parent)" :key="opt.id" :value="opt.id">{{ opt.nameEN || opt.nameTA }}</option>
                            </select>
                            <button class="ac-assign" :disabled="!assignSel[parent.id] || busyId === parent.id" @click="assignSub(parent.id)">
                                {{ t('Add', 'சேர்') }}
                            </button>
                        </div>
                    </div>

                    <div v-if="parent.children && parent.children.items && parent.children.items.length" class="ac-child-list">
                        <div v-for="child in parent.children.items" :key="child.id" class="ac-child-row">
                            <div class="ac-fields">
                                <label>
                                    <span>EN</span>
                                    <input v-model="edits[child.id].nameEN" />
                                </label>
                                <label>
                                    <span>TA</span>
                                    <input v-model="edits[child.id].nameTA" />
                                </label>
                            </div>
                            <label class="ac-switch" :title="t('Show this category on the home page', 'இந்த வகையை முகப்பில் காட்டு')">
                                <input type="checkbox" :checked="!!child.isFeatured" :disabled="busyId === child.id" @change="toggleFeatured(child)" />
                                <span class="ac-switch-track"><span class="ac-switch-thumb"></span></span>
                                <span class="ac-switch-text">{{ t('Featured', 'சிறப்பு') }}</span>
                            </label>
                            <div class="ac-card-actions">
                                <button class="ac-save" :disabled="!isChanged(child) || busyId === child.id" @click="saveCategory(child)">
                                    <vue-feather v-if="busyId === child.id" type="loader" animation="spin" size="13"></vue-feather>
                                    <vue-feather v-else type="save" size="13"></vue-feather>
                                    {{ t('Save', 'சேமி') }}
                                </button>
                                <button class="ac-ghost" :disabled="busyId === child.id" @click="unassignSub(child.id)" :title="t('Move to top level', 'மேல் மட்டத்திற்கு நகர்த்து')">
                                    <vue-feather type="corner-left-up" size="13"></vue-feather>
                                </button>
                                <button class="ac-delete" :disabled="busyId === child.id" @click="deleteCategory(child)" :title="t('Delete', 'நீக்கு')">
                                    <vue-feather type="trash-2" size="13"></vue-feather>
                                </button>
                            </div>
                            <button class="ac-id" :title="t('Click to copy', 'நகலெடுக்க கிளிக் செய்யவும்')" @click="copyId(child.id)">ID: {{ child.id }}</button>
                        </div>
                    </div>
                    <span v-else class="ac-subs-empty">{{ t('No sub-categories yet', 'துணை வகைகள் இல்லை') }}</span>
                </div>
            </article>
        </div>
    </div>
</template>

<script>
import { API } from 'aws-amplify';
import { listCategoryModelsWithChildren } from '../graphql/custom.js';
import { createCategoryModel, updateCategoryModel, deleteCategoryModel } from '../graphql/mutations.js';
import ShortUniqueId from 'short-unique-id';

const uid = new ShortUniqueId();

export default {
    name: 'categories-view',
    inject: {
        languageRef: { from: 'currentLanguage', default: 'EN' }
    },
    data() {
        return {
            categories: [],
            edits: {},
            assignSel: {},
            newCategory: { nameEN: '', nameTA: '' },
            search: '',
            loading: false,
            creating: false,
            busyId: null,
            alert: null,
        };
    },
    computed: {
        currentLanguage() {
            return this.languageRef || 'EN';
        },
        isInvalidForm() {
            return !this.newCategory.nameEN.trim() || !this.newCategory.nameTA.trim();
        },
        // Top-level categories only (a sub-category has categoryModelChildrenId set).
        parentCategories() {
            return this.categories.filter(c => !c.categoryModelChildrenId);
        },
        filteredParents() {
            const q = this.search.trim().toLowerCase();
            const match = (c) => (c.nameEN || '').toLowerCase().includes(q) || (c.nameTA || '').toLowerCase().includes(q);
            if (!q) return this.parentCategories;
            // Keep a parent if it matches, or any of its sub-categories match.
            return this.parentCategories.filter(p => match(p) || (p.children?.items || []).some(match));
        },
    },
    created() {
        this.fetchCategories();
    },
    methods: {
        t(en, ta) {
            return this.currentLanguage === 'TA' ? ta : en;
        },
        setAlert(type, text) {
            this.alert = { type, text };
            if (type === 'success') setTimeout(() => { this.alert = null; }, 3000);
        },
        async copyId(id) {
            try {
                await navigator.clipboard.writeText(id);
                this.setAlert('success', this.t('Category ID copied.', 'வகை ID நகலெடுக்கப்பட்டது.'));
            } catch {
                this.setAlert('error', this.t('Could not copy the ID.', 'ID நகலெடுக்க முடியவில்லை.'));
            }
        },
        async fetchCategories() {
            this.loading = true;
            try {
                const res = await API.graphql({
                    query: listCategoryModelsWithChildren,
                    variables: { limit: 500 }
                });
                this.categories = res.data.listCategoryModels.items || [];
                // Build editable copies + assign selectors. Also seed entries for
                // nested children, which may fall outside the top-level page.
                const edits = {};
                const assignSel = {};
                for (const c of this.categories) {
                    edits[c.id] = { nameEN: c.nameEN || '', nameTA: c.nameTA || '' };
                    assignSel[c.id] = null;
                    for (const child of (c.children?.items || [])) {
                        if (!edits[child.id]) {
                            edits[child.id] = { nameEN: child.nameEN || '', nameTA: child.nameTA || '' };
                        }
                    }
                }
                this.edits = edits;
                this.assignSel = assignSel;
            } catch (error) {
                console.error('Failed to load categories:', error);
                this.setAlert('error', this.t('Failed to load categories.', 'வகைகளை ஏற்ற முடியவில்லை.'));
            } finally {
                this.loading = false;
            }
        },
        isChanged(cat) {
            const e = this.edits[cat.id];
            return !!e && (e.nameEN !== (cat.nameEN || '') || e.nameTA !== (cat.nameTA || ''));
        },
        assignableFor(parent) {
            // Offer only other top-level categories that aren't parents themselves,
            // so we don't create deep nesting when promoting one into a sub-category.
            return this.categories.filter(c =>
                c.id !== parent.id &&
                !c.categoryModelChildrenId &&
                !(c.children && c.children.items && c.children.items.length));
        },
        async mutate(input, action) {
            return API.graphql({
                query: action,
                variables: { input },
                authMode: 'AMAZON_COGNITO_USER_POOLS',
            });
        },
        async createCategory() {
            if (this.isInvalidForm) return;
            this.creating = true;
            try {
                await this.mutate({
                    id: uid(),
                    nameEN: this.newCategory.nameEN.trim(),
                    nameTA: this.newCategory.nameTA.trim(),
                }, createCategoryModel);
                this.newCategory = { nameEN: '', nameTA: '' };
                this.setAlert('success', this.t('Category created.', 'வகை உருவாக்கப்பட்டது.'));
                await this.fetchCategories();
            } catch (error) {
                console.error('Failed to create category:', error);
                this.setAlert('error', this.t('Failed to create category.', 'வகையை உருவாக்க முடியவில்லை.'));
            } finally {
                this.creating = false;
            }
        },
        async toggleFeatured(cat) {
            const next = !cat.isFeatured;
            this.busyId = cat.id;
            try {
                await this.mutate({ id: cat.id, isFeatured: next }, updateCategoryModel);
                cat.isFeatured = next;
                this.setAlert('success', next
                    ? this.t('Added to the home page.', 'முகப்பில் சேர்க்கப்பட்டது.')
                    : this.t('Removed from the home page.', 'முகப்பிலிருந்து நீக்கப்பட்டது.'));
            } catch (error) {
                console.error('Failed to toggle featured:', error);
                this.setAlert('error', this.t('Failed to update featured status.', 'சிறப்பு நிலையை புதுப்பிக்க முடியவில்லை.'));
            } finally {
                this.busyId = null;
            }
        },
        async saveCategory(cat) {
            const e = this.edits[cat.id];
            if (!e || !e.nameEN.trim() || !e.nameTA.trim()) {
                this.setAlert('error', this.t('Both names are required.', 'இரு பெயர்களும் தேவை.'));
                return;
            }
            this.busyId = cat.id;
            try {
                await this.mutate({ id: cat.id, nameEN: e.nameEN.trim(), nameTA: e.nameTA.trim() }, updateCategoryModel);
                cat.nameEN = e.nameEN.trim();
                cat.nameTA = e.nameTA.trim();
                this.setAlert('success', this.t('Saved.', 'சேமிக்கப்பட்டது.'));
            } catch (error) {
                console.error('Failed to update category:', error);
                this.setAlert('error', this.t('Failed to save category.', 'வகையை சேமிக்க முடியவில்லை.'));
            } finally {
                this.busyId = null;
            }
        },
        async deleteCategory(cat) {
            const childCount = cat.children?.items?.length || 0;
            const msg = childCount
                ? this.t(`Delete "${cat.nameEN || cat.nameTA}"? Its ${childCount} sub-category link(s) will be detached.`,
                    `"${cat.nameEN || cat.nameTA}" ஐ நீக்கவா? அதன் ${childCount} துணை வகை இணைப்புகள் நீக்கப்படும்.`)
                : this.t(`Delete "${cat.nameEN || cat.nameTA}"?`, `"${cat.nameEN || cat.nameTA}" ஐ நீக்கவா?`);
            if (!window.confirm(msg)) return;

            this.busyId = cat.id;
            try {
                // Detach children first so they aren't left pointing at a missing parent.
                for (const child of (cat.children?.items || [])) {
                    await this.mutate({ id: child.id, categoryModelChildrenId: null }, updateCategoryModel);
                }
                await this.mutate({ id: cat.id }, deleteCategoryModel);
                this.categories = this.categories.filter(c => c.id !== cat.id);
                this.setAlert('success', this.t('Category deleted.', 'வகை நீக்கப்பட்டது.'));
            } catch (error) {
                console.error('Failed to delete category:', error);
                this.setAlert('error', this.t('Failed to delete category.', 'வகையை நீக்க முடியவில்லை.'));
            } finally {
                this.busyId = null;
            }
        },
        async assignSub(parentId) {
            const childId = this.assignSel[parentId];
            if (!childId) return;
            this.busyId = parentId;
            try {
                await this.mutate({ id: childId, categoryModelChildrenId: parentId }, updateCategoryModel);
                this.assignSel[parentId] = null;
                await this.fetchCategories();
                this.setAlert('success', this.t('Sub-category added.', 'துணை வகை சேர்க்கப்பட்டது.'));
            } catch (error) {
                console.error('Failed to assign sub-category:', error);
                this.setAlert('error', this.t('Failed to add sub-category.', 'துணை வகையைச் சேர்க்க முடியவில்லை.'));
            } finally {
                this.busyId = null;
            }
        },
        async unassignSub(childId) {
            this.busyId = childId;
            try {
                await this.mutate({ id: childId, categoryModelChildrenId: null }, updateCategoryModel);
                await this.fetchCategories();
                this.setAlert('success', this.t('Sub-category removed.', 'துணை வகை நீக்கப்பட்டது.'));
            } catch (error) {
                console.error('Failed to unassign sub-category:', error);
                this.setAlert('error', this.t('Failed to remove sub-category.', 'துணை வகையை நீக்க முடியவில்லை.'));
            } finally {
                this.busyId = null;
            }
        },
    },
};
</script>

<style scoped>
.admin-categories {
    max-width: 900px;
}

.ac-header {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    align-items: flex-start;
    margin-bottom: 20px;
}

.ac-eyebrow {
    display: inline-flex;
    margin-bottom: 6px;
    color: #ff1314;
    font-size: 12px;
    font-weight: 800;
    text-transform: uppercase;
}

.ac-header h1 {
    margin: 0;
    color: var(--text-primary);
    font-size: 26px;
}

.ac-header p {
    max-width: 620px;
    margin: 8px 0 0;
    color: var(--text-secondary);
    font-size: 14px;
    line-height: 1.5;
}

.ac-refresh {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: 38px;
    padding: 0 14px;
    border-radius: 6px;
    border: 1px solid var(--border-color);
    background: var(--bg-card);
    color: var(--text-primary);
    font-weight: 700;
    cursor: pointer;
    flex-shrink: 0;
}

.ac-create {
    background: var(--bg-card);
    border: 1px solid var(--border-color);
    border-radius: 10px;
    padding: 16px;
    margin-bottom: 18px;
}

.ac-create h3 {
    margin: 0 0 12px;
    color: var(--text-primary);
    font-size: 16px;
}

.ac-create-row {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
}

.ac-create-row input {
    flex: 1;
    min-width: 160px;
}

.admin-categories input,
.admin-categories select {
    min-height: 40px;
    padding: 0 12px;
    border-radius: 6px;
    border: 1px solid var(--border-color);
    background: var(--bg-tertiary);
    color: var(--text-primary);
    font-size: 14px;
}

.admin-categories input:focus,
.admin-categories select:focus {
    outline: none;
    border-color: #ff1314;
}

.ac-primary,
.ac-assign,
.ac-save {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: 40px;
    padding: 0 16px;
    border: 0;
    border-radius: 6px;
    background: #ff1314;
    color: #fff;
    font-weight: 700;
    cursor: pointer;
}

.ac-primary:disabled,
.ac-assign:disabled,
.ac-save:disabled,
.ac-refresh:disabled,
.ac-delete:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.ac-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
}

.ac-search {
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    padding: 0 12px;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    background: var(--bg-tertiary);
    color: var(--text-tertiary);
}

.ac-search input {
    flex: 1;
    border: 0;
    background: transparent;
    min-height: 40px;
}

.ac-search input:focus {
    border: 0;
}

.ac-count {
    color: var(--text-tertiary);
    font-size: 13px;
    font-weight: 700;
    white-space: nowrap;
}

.ac-alert {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 14px;
    border-radius: 8px;
    font-weight: 700;
    margin-bottom: 14px;
}

.ac-alert.error {
    color: #b42318;
    background: rgba(255, 19, 20, 0.1);
}

.ac-alert.success {
    color: #116329;
    background: rgba(34, 197, 94, 0.12);
}

.ac-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 48px 20px;
    color: var(--text-secondary);
    text-align: center;
}

.ac-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.ac-card {
    background: var(--bg-card);
    border: 1px solid var(--border-color);
    border-radius: 10px;
    padding: 14px 16px;
}

.ac-card-main {
    display: flex;
    align-items: flex-end;
    gap: 14px;
}

.ac-fields {
    display: flex;
    gap: 10px;
    flex: 1;
    flex-wrap: wrap;
}

.ac-fields label {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    min-width: 150px;
    font-size: 11px;
    font-weight: 800;
    color: var(--text-tertiary);
    text-transform: uppercase;
}

.ac-card-actions {
    display: flex;
    gap: 8px;
}

.ac-delete {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 40px;
    width: 40px;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    background: var(--bg-tertiary);
    color: #c8191e;
    cursor: pointer;
}

.ac-delete:hover:not(:disabled) {
    background: rgba(255, 19, 20, 0.1);
}

.ac-switch {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    user-select: none;
    flex-shrink: 0;
}

.ac-switch input {
    position: absolute;
    width: 0;
    height: 0;
    min-height: 0;
    padding: 0;
    opacity: 0;
}

.ac-switch-track {
    position: relative;
    width: 40px;
    height: 22px;
    border-radius: 999px;
    background: var(--border-color);
    transition: background 0.2s;
    flex-shrink: 0;
}

.ac-switch-thumb {
    position: absolute;
    top: 2px;
    left: 2px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
    transition: transform 0.2s;
}

.ac-switch input:checked + .ac-switch-track {
    background: #16a34a;
}

.ac-switch input:checked + .ac-switch-track .ac-switch-thumb {
    transform: translateX(18px);
}

.ac-switch input:disabled + .ac-switch-track {
    opacity: 0.55;
}

.ac-switch-text {
    font-size: 12px;
    font-weight: 800;
    text-transform: uppercase;
    color: var(--text-tertiary);
}

.ac-switch input:checked ~ .ac-switch-text {
    color: #16a34a;
}

.ac-subs {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid var(--border-color);
}

.ac-subs-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 10px;
}

.ac-subs-label {
    font-size: 12px;
    font-weight: 800;
    color: var(--text-tertiary);
    text-transform: uppercase;
}

.ac-subs-empty {
    display: block;
    color: var(--text-tertiary);
    font-size: 13px;
}

.ac-child-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.ac-id {
    display: block;
    margin-top: 6px;
    padding: 0;
    border: none;
    background: none;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 11px;
    color: var(--text-tertiary);
    cursor: pointer;
    text-align: left;
}

.ac-id:hover {
    color: var(--text-secondary);
}

.ac-child-row .ac-id {
    width: 100%;
    order: 10;
    margin-top: 0;
}

.ac-child-row {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 8px;
    background: var(--bg-tertiary);
    border-left: 3px solid var(--border-color);
}

.ac-ghost {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-height: 40px;
    width: 40px;
    border: 1px solid var(--border-color);
    border-radius: 6px;
    background: var(--bg-card);
    color: var(--text-secondary);
    cursor: pointer;
}

.ac-ghost:hover:not(:disabled) {
    color: var(--text-primary);
    border-color: var(--text-tertiary);
}

.ac-ghost:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

.ac-add-sub {
    display: flex;
    gap: 8px;
}

.ac-add-sub select {
    max-width: 240px;
}

@media (max-width: 640px) {
    .ac-card-main,
    .ac-child-row {
        flex-direction: column;
        align-items: stretch;
    }

    .ac-add-sub {
        width: 100%;
    }

    .ac-add-sub select {
        flex: 1;
        max-width: none;
    }
}
</style>
